import React from 'react'
import {useIntl} from 'react-intl'
import {startCase} from 'lodash'
import {
  Condition,
  ConditionDataType,
  ConditionField,
  ConditionFieldMetadata,
  OPERATORS_REQUIRING_NULL_VALUE,
  OPTION_COUNTRIES,
  usesValueTo,
} from '../../../../../../_gori/constants'
import {InputTextFormik, SelectFormik} from '../../../../../../_gori/partials/widgets'
import {InputDate} from '../../../../../../_gori/partials/widgets/form/InputDate'
import {InputRadioFormik} from '../../../../../../_gori/partials/widgets/form/InputRadioFormik'
import {OPTION_ITEM_TYPE} from '../../../../claims/core/_const'
import {useAutomationRulesData} from '../../../hooks'
import {AddressOptionLabel} from './AddressOptionLabel'
type Props = {
  condition: Partial<Condition>
  index: number
  formik: any
  fieldMeta?: ConditionFieldMetadata
  ghostShipFromOptions?: {label: string; value: any}[]
  ghostPackageOptions?: {label: string; value: any}[]
}

export const ConditionValue: React.FC<Props> = ({
  condition,
  index,
  formik,
  fieldMeta,
  ghostShipFromOptions = [],
  ghostPackageOptions = [],
}) => {
  const intl = useIntl()

  const {carrierOptions, serviceOptions, shipFromOptions, packageOptions, integrationStores} =
    useAutomationRulesData()
  if (!fieldMeta || !condition.operator) return null
  if (OPERATORS_REQUIRING_NULL_VALUE.includes(condition.operator as any)) return null

  const needsValueTo = usesValueTo(condition.operator)

  const isSelectField =
    fieldMeta.data_type === ConditionDataType.MULTI_SELECT ||
    fieldMeta.data_type === ConditionDataType.DROPDOWN

  const renderMainContent = () => {
    if (isSelectField) {
      const selectFormikProps: React.ComponentProps<typeof SelectFormik> = {
        formik,
        name: `conditions[${index}].value`,
        options: [],
        isMulti: fieldMeta.data_type === ConditionDataType.MULTI_SELECT,
        emptyDefault: false,
      }

      switch (fieldMeta.field) {
        case ConditionField.MARKETPLACE: {
          selectFormikProps.options = Object.entries(integrationStores || {})
            .filter(([_, stores]: [string, any]) => stores.length > 0)
            .map(([key]) => ({label: startCase(key), value: key}))
          break
        }
        case ConditionField.STORE_NAME: {
          selectFormikProps.options = (
            Object.entries(integrationStores || {}) as [string, any[]][]
          ).flatMap(([key, stores]) =>
            stores.map((store) => ({
              label: `${startCase(key)} - ${store.store_name}`,
              value: store.id,
            }))
          )
          break
        }
        case ConditionField.MERCHANDISE_CATEGORY: {
          selectFormikProps.options = OPTION_ITEM_TYPE
          selectFormikProps.hasUseIntl = true
          break
        }
        case ConditionField.DESTINATION_COUNTRY: {
          selectFormikProps.options = OPTION_COUNTRIES
          break
        }
        case ConditionField.CARRIER:
          selectFormikProps.options = carrierOptions
          break
        case ConditionField.SHIPPING_SERVICE:
          selectFormikProps.options = serviceOptions as any
          break
        case ConditionField.SHIP_FROM_LOCATION:
          selectFormikProps.options = [...shipFromOptions, ...ghostShipFromOptions]
          selectFormikProps.formatOptionLabel = (option, {selectValue, context}) => (
            <AddressOptionLabel
              option={option}
              isSelected={(selectValue as any[]).some((v) => v.value === option.value)}
              context={context}
            />
          )
          break
        case ConditionField.PACKAGE_DIMENSIONS:
          selectFormikProps.options = [...packageOptions, ...ghostPackageOptions]
          break
        default:
          break
      }

      return <SelectFormik {...selectFormikProps} />
    }

    switch (fieldMeta.data_type) {
      case ConditionDataType.NUMBER:
        return (
          <>
            <InputTextFormik
              formik={formik}
              name={`conditions[${index}].value`}
              type='number'
              placeholder={intl.formatMessage({id: 'VALUE'})}
            />
            {needsValueTo && (
              <InputTextFormik
                formik={formik}
                name={`conditions[${index}].value_to`}
                type='number'
                placeholder={intl.formatMessage({id: 'VALUE_TO'})}
                className='mt-2'
              />
            )}
          </>
        )
      case ConditionDataType.DATE:
        return (
          <>
            <InputDate
              formik={formik}
              name={`conditions[${index}].value`}
              views={['year', 'month', 'day']}
            />
            {needsValueTo && (
              <InputDate
                formik={formik}
                name={`conditions[${index}].value_to`}
                views={['year', 'month', 'day']}
                className='mt-2'
              />
            )}
          </>
        )
      case ConditionDataType.BOOLEAN:
        return (
          <div className='d-flex gap-4 align-items-center h-100'>
            <InputRadioFormik
              formik={formik}
              name={`conditions[${index}].value`}
              label={intl.formatMessage({id: 'YES'})}
              valueInput={true}
              onChange={() => formik.setFieldValue(`conditions[${index}].value`, true)}
            />
            <InputRadioFormik
              formik={formik}
              name={`conditions[${index}].value`}
              label={intl.formatMessage({id: 'NO'})}
              valueInput={false}
              onChange={() => formik.setFieldValue(`conditions[${index}].value`, false)}
            />
          </div>
        )
      default:
        return (
          <InputTextFormik
            formik={formik}
            name={`conditions[${index}].value`}
            placeholder={intl.formatMessage({id: 'VALUE'})}
          />
        )
    }
  }

  return <>{renderMainContent()}</>
}
